import { get, uniqBy } from 'lodash';
import moment, { Moment } from 'moment';
import { EquipmentType } from '../interfaces/model';
import { Publish } from '../interfaces/publish';

// How to read a device publish: when the thing it describes happened, what kind of statistics it
// carries, which alert it reports, and whether it repeats a row we already have. Every product
// packs these differently, and differently from what the field names suggest (in most
// statistics data.time is the moment the device sent them), so the differences live here, one
// profile per product. Callers ask these questions instead of reading published_at, sk,
// data.time, data.alert, statistics_type/type or fw_version themselves.
//
// published_at and sk are when a row reached us. A device can send an event long after it
// happened (after an outage, or from a queue), and Particle can hand us the same publish twice.
//
// What each profile reads was taken from the product's firmware and checked against rows in
// data-table-prod on 2026-09-21. Shared by the client and the server, so it must stay free of UI
// and server imports.

export type Product =
  | 'utility'
  | 'aerasOne'
  | 'chair'
  | 'smartChair'
  | 'handpiece'
  | 'sterilizer';

// Record<EquipmentType, ...> makes the compiler reject a new equipment type with no product.
const PRODUCT_BY_TYPE: Record<EquipmentType, Product> = {
  compressor: 'utility',
  vacuum: 'utility',
  aerasone_compressor: 'aerasOne',
  aerasone_vacuum: 'aerasOne',
  chair: 'chair',
  handpiece: 'handpiece',
  sterilizer: 'sterilizer',
};

// The product a piece of equipment is, from its model's type. Partner smart chairs are chair
// models, but their rows are written by the server from the partner's events.
export const productOf = (
  modelType?: string,
  equipment?: { isSmartChair?: boolean }
): Product | undefined => {
  if (get(equipment, 'isSmartChair')) {
    return 'smartChair';
  }
  return modelType && PRODUCT_BY_TYPE.hasOwnProperty(modelType)
    ? PRODUCT_BY_TYPE[modelType as EquipmentType]
    : undefined;
};

// A time, and whether it came from the event itself or only from when its row reached us.
export interface EventTime {
  at: Moment;
  basis: 'event' | 'arrival';
}

export type EventKind =
  | 'alert'
  | 'statistics'
  | 'instant'
  | 'config'
  | 'maint'
  | 'thresholds'
  | 'testresults'
  | 'other';

// By the end of the event name, which every product shares and which survives the renaming the
// client's store does (chair_sensor_statistics becomes dz_sensor_statistics there).
export const kindOf = (row: Publish): EventKind => {
  const event = `${get(row, 'event', '')}`;
  const kinds: Array<[RegExp, EventKind]> = [
    [/_alert$/, 'alert'],
    [/_sensor_statistics$/, 'statistics'],
    [/_sensor_instant$/, 'instant'],
    [/_config$/, 'config'],
    [/_maint$/, 'maint'],
    [/_thresholds$/, 'thresholds'],
    [/_testresults$/, 'testresults'],
  ];
  const found = kinds.find(([pattern]) => pattern.test(event));
  return found ? found[1] : 'other';
};

export interface Alert {
  source?: string;
  error?: number;
  value?: number;
  threshold?: number;
  occurred: EventTime;
}

export type StatisticsKind = 'daily' | 'hourly' | 'session' | 'debug';

export interface Statistics {
  // undefined for a type the product does not define
  kind?: StatisticsKind;
  // the end of the period covered, or the device's last update of it
  end: EventTime;
  // its start, where the product reports one that can be trusted
  start?: Moment;
  // daily only: a moment inside the day covered, placed so that its date in any US time zone is
  // that day (the day's own time zone is the device's, which we do not know)
  day?: Moment;
}

interface Profile {
  // when what the row describes happened; the arrival time where the product does not say
  occurred(row: Publish): EventTime;
  alert(row: Publish): Alert;
  statistics(row: Publish): Statistics;
  // the fields that make two rows the same event when the device sent it twice, which gives the
  // copy a new seq and published_at; undefined where nothing but the delivery identifies it
  sameEvent(row: Publish): unknown[] | undefined;
}

const arrival = (row: Publish): EventTime => ({
  at: moment(row.published_at || row.sk),
  basis: 'arrival',
});

// A device's epoch seconds, or undefined where there is no time to read: the field is missing or
// zero (a device with no valid clock leaves it out or sends 0), or it is from before any of these
// products existed. An unsynced Particle clock reads as 1970 plus how long the device has been up,
// and taking one of those as real would date the row to 1970, which also freezes its cached copy,
// since nothing later can replace a copy that claims to be older than everything. A genuinely late
// event still carries a real time: the latest measured was a utility daily 188 days old.
const DEVICE_TIME_FLOOR = Date.UTC(2020, 0, 1) / 1000;
const secondsAt = (value: unknown): Moment | undefined => {
  const seconds = Number(value);
  return value !== undefined && value !== null && seconds >= DEVICE_TIME_FLOOR
    ? moment(seconds * 1000)
    : undefined;
};

// A device's time for an event, unless there is none or it is after the row arrived by more than a
// clock running a little fast explains: an event cannot happen after it reached us, and a time
// from a wrong clock must not outrank everything that arrives after it.
const CLOCK_AHEAD_LIMIT_HOURS = 1;
const fromEvent = (value: unknown, row: Publish): EventTime => {
  const at = secondsAt(value);
  const arrived = arrival(row);
  return at &&
    !at.isAfter(arrived.at.clone().add(CLOCK_AHEAD_LIMIT_HOURS, 'hours'))
    ? { at, basis: 'event' }
    : arrived;
};

const plusHours = (at: Moment | undefined, hours: number) =>
  at ? at.clone().add(hours, 'hours') : undefined;

// The values that identify an event, or undefined when one is missing: a row without them (a
// clear the web app posted, statistics from a device with no clock) is only its delivery.
const allOf = (...values: unknown[]) =>
  values.every((v) => v !== undefined && v !== null) ? values : undefined;

// data.source / error / value / threshold at the top of the payload, as every product but the
// handpiece sends them
const topLevelAlert = (row: Publish, occurred: EventTime): Alert => ({
  source: get(row, 'data.source'),
  error: get(row, 'data.error'),
  value: get(row, 'data.value'),
  threshold: get(row, 'data.threshold'),
  occurred,
});

// A product we have not read the firmware of: data.time where there is one, which is what the
// app has always assumed.
const fallback: Profile = {
  occurred: (row) => fromEvent(get(row, 'data.time'), row),
  alert: (row) => topLevelAlert(row, fromEvent(get(row, 'data.time'), row)),
  statistics: (row) => ({ end: fromEvent(get(row, 'data.time'), row) }),
  sameEvent: () => undefined,
};

// Compressors and vacuums (dentalez-fw). An alert's time is when its threshold tripped or cleared
// (user_app_threshold.cpp:1220, 1421); one that could not be sent waits in FRAM, across restarts,
// and keeps that time. Statistics cover start_time to end_time on the device's local clock, and
// their data.time is the moment of sending (user_app_accumulators.cpp:444-453). An hourly period
// only closes when the equipment ran, so it can span several hours. Config and schedule carry no
// time at all.
const UTILITY_STATISTICS: StatisticsKind[] = ['daily', 'hourly', 'debug'];
const utility: Profile = {
  occurred: (row) =>
    kindOf(row) === 'statistics'
      ? utility.statistics(row).end
      : fromEvent(get(row, 'data.time'), row),
  alert: (row) => topLevelAlert(row, fromEvent(get(row, 'data.time'), row)),
  statistics: (row) => {
    const kind = UTILITY_STATISTICS[get(row, 'data.statistics_type')];
    const start = secondsAt(get(row, 'data.start_time'));
    const end = fromEvent(get(row, 'data.end_time'), row);
    // a day starts at the device's midnight, so twelve hours on is its noon
    const day =
      kind === 'daily'
        ? plusHours(start, 12) || plusHours(end.at, -12)
        : undefined;
    return { kind, start, end, day };
  },
  sameEvent: (row) =>
    kindOf(row) === 'alert'
      ? allOf(
          get(row, 'data.source'),
          get(row, 'data.error'),
          get(row, 'data.time')
        )
      : kindOf(row) === 'statistics'
        ? allOf(
            get(row, 'data.statistics_type'),
            get(row, 'data.start_time'),
            get(row, 'data.end_time')
          )
        : undefined,
};

// Aeras One (aeras-one). An ai_alert payload holds "time" twice, the moment of sending and then
// the alert's own time (App_cloud.cpp:1165, 1181); JSON.parse keeps the last, so data.time is
// the alert's. Pending alerts are kept in flash across restarts. A manual clear_alert repeats the
// trip's time rather than the clear's (SignalThresholdState.cpp:32-39). Statistics run from start
// to last_updated on UTC hours and days, name their type in words, and have data.time as the
// moment of sending (App_cloud.cpp:1518-1524).
const aerasOne: Profile = {
  occurred: (row) =>
    kindOf(row) === 'statistics'
      ? aerasOne.statistics(row).end
      : fromEvent(get(row, 'data.time'), row),
  alert: (row) => topLevelAlert(row, fromEvent(get(row, 'data.time'), row)),
  statistics: (row) => {
    const type = get(row, 'data.statistics_type');
    const kind = UTILITY_STATISTICS.indexOf(type) > -1 ? type : undefined;
    const start = secondsAt(get(row, 'data.start'));
    const end = fromEvent(get(row, 'data.last_updated'), row);
    const day =
      kind === 'daily'
        ? plusHours(start, 12) || plusHours(end.at, -12)
        : undefined;
    return { kind, start, end, day };
  },
  sameEvent: (row) =>
    kindOf(row) === 'alert'
      ? allOf(
          get(row, 'data.source'),
          get(row, 'data.error'),
          get(row, 'data.time'),
          // null when the alert cleared for lack of a reading (aeras-one bc63ec0)
          get(row, 'data.threshold', null) === null
            ? 'none'
            : get(row, 'data.threshold')
        )
      : kindOf(row) === 'statistics'
        ? allOf(
            get(row, 'data.statistics_type'),
            get(row, 'data.start'),
            get(row, 'data.last_updated')
          )
        : undefined,
};

// The 6400 chair (dentalez-6400-chair-iot). An alert's data.time is the moment it was sent
// (cloud_msg.cpp); from product version 31 it also carries alert_time, when the chair registered the
// change (alert_publish.h), and one without is dated by its arrival. A backlog goes out lowest bit
// first, not oldest first (TRY_DIRTY_BIT in main.cpp), and several bits share a source (LP's four
// thresholds and its disconnect), so an older alert_time can arrive after a newer one (see
// orderOf). Two alerts can share source, error and alert_time (BACKS upper and lower clearing in
// one second), so only the delivery identifies one. Statistics wait in a queue until acknowledged
// from product version 30 (AERFW-79) and are re-sent byte for byte, so a copy has the same type,
// time and session count (stats_outbox.h:17-22). A session covers start_time to end_time from v30; before, only
// data.time, which is its end plus the three-minute exit countdown. A daily's time is the local
// midnight that closed its day (stats_persist.cpp:290; before v30, the clock at that midnight). The
// types were renumbered in version 10 (AERFW-48).
const chair: Profile = {
  occurred: (row) =>
    kindOf(row) === 'statistics'
      ? chair.statistics(row).end
      : kindOf(row) === 'alert'
        ? chair.alert(row).occurred
        : fromEvent(get(row, 'data.time'), row),
  alert: (row) =>
    topLevelAlert(row, fromEvent(get(row, 'data.alert_time'), row)),
  statistics: (row) => {
    const names: StatisticsKind[] =
      Number(get(row, 'fw_version', 10)) < 10
        ? ['debug', 'daily', 'session']
        : ['daily', 'session', 'debug'];
    const kind = names[get(row, 'data.type')];
    const end = fromEvent(
      get(row, 'data.end_time') || get(row, 'data.time'),
      row
    );
    const start = secondsAt(get(row, 'data.start_time'));
    const day = kind === 'daily' ? plusHours(end.at, -12) : undefined;
    return { kind, start, end, day };
  },
  sameEvent: (row) =>
    kindOf(row) === 'statistics'
      ? allOf(
          get(row, 'data.type'),
          get(row, 'data.time'),
          get(row, 'data.session_count.0')
        )
      : undefined,
};

// Partner smart chairs. The server writes one daily row per batch the partner posts
// (server/src/controllers/external/smartchair.ts), stamped with the time it arrived and no
// data.time; each of the partner's sessions has its own ISO time and a duration in seconds or
// h:m:s.
const durationSeconds = (duration: unknown): number => {
  if (typeof duration === 'number') {
    return duration;
  }
  const [h, m, s] = `${duration}`.split(':').map(Number);
  return (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
};
const smartChair: Profile = {
  occurred: (row) =>
    kindOf(row) === 'statistics'
      ? smartChair.statistics(row).end
      : arrival(row),
  alert: (row) => topLevelAlert(row, arrival(row)),
  statistics: (row) => {
    const sessions = (
      get(row, 'raw.events', []) as Array<{
        time?: string;
        duration?: unknown;
      }>
    ).filter((e) => e.time && moment(e.time).isValid());
    if (!sessions.length) {
      return { kind: 'daily', end: arrival(row) };
    }
    const starts = sessions.map((e) => moment(e.time).valueOf());
    const ends = sessions.map(
      (e) => moment(e.time).valueOf() + durationSeconds(e.duration) * 1000
    );
    const start = moment(Math.min(...starts));
    return {
      kind: 'daily',
      start,
      end: { at: moment(Math.max(...ends)), basis: 'event' },
      day: start.clone(),
    };
  },
  sameEvent: () => undefined,
};

// Handpieces (dentalez-handpieces-fw), whose publishes pass through server/controllers/conversion
// on the way in. Statistics come out flattened; their data.time is the moment of sending and
// end_time the last time the motor ran (Accumulators.cpp:42-44, 249-259). A session's start_time
// can be months old after a restart restored it, so it is not reported. A daily's start_time is
// when its day was started, the device's midnight or its first moment on that day. Alerts are not
// flattened: the alert and its time are in data.alert, and data.time is the moment of sending
// (HandPiece.cpp:515-518). One the web app posts itself (a manual clear) has them at the top.
const HANDPIECE_STATISTICS: StatisticsKind[] = ['daily', 'session', 'debug'];
const handpiece: Profile = {
  occurred: (row) =>
    kindOf(row) === 'statistics'
      ? handpiece.statistics(row).end
      : kindOf(row) === 'alert'
        ? handpiece.alert(row).occurred
        : fromEvent(get(row, 'data.time'), row),
  alert: (row) => {
    const alert = get(row, 'data.alert');
    if (!alert) {
      return topLevelAlert(row, fromEvent(get(row, 'data.time'), row));
    }
    return {
      source: get(alert, 'source'),
      error: get(alert, 'error'),
      value: get(alert, 'value'),
      threshold: get(alert, 'threshold'),
      occurred: fromEvent(get(alert, 'time'), row),
    };
  },
  statistics: (row) => {
    const kind = HANDPIECE_STATISTICS[get(row, 'data.statistics_type')];
    const end = fromEvent(get(row, 'data.end_time'), row);
    if (kind !== 'daily') {
      return { kind, end };
    }
    const start = secondsAt(get(row, 'data.start_time'));
    return { kind, start, end, day: start || plusHours(end.at, -12) };
  },
  sameEvent: (row) => {
    if (kindOf(row) === 'alert') {
      const alert = handpiece.alert(row);
      return alert.occurred.basis === 'event'
        ? allOf(alert.source, alert.error, alert.occurred.at.valueOf())
        : undefined;
    }
    return kindOf(row) === 'statistics'
      ? allOf(
          get(row, 'data.statistics_type'),
          get(row, 'data.start_time'),
          get(row, 'data.end_time')
        )
      : undefined;
  },
};

const PROFILES: Record<Product, Profile> = {
  utility,
  aerasOne,
  chair,
  smartChair,
  handpiece,
  // the sterilizer firmware has not been read
  sterilizer: fallback,
};

const profileFor = (product?: Product): Profile =>
  (product && PROFILES[product]) || fallback;

// When what the row describes happened, as well as the product lets us tell.
export const occurredAt = (product: Product | undefined, row: Publish) =>
  profileFor(product).occurred(row);

export const alertOf = (product: Product | undefined, row: Publish) =>
  profileFor(product).alert(row);

export const statisticsOf = (product: Product | undefined, row: Publish) =>
  profileFor(product).statistics(row);

// Which of a device's rows of one event, or alerts of one source, is the current one: the one
// with the latest order. For an alert that is its arrival, not its time. The utility and chair
// firmware send a backlog in their own priority order, not oldest first, several of a source's
// thresholds can be pending at once, and a clear does not say which threshold cleared. The
// utility's alert time is also the moment of sending for a threshold it announces without a
// change (t0 of 0, user_app.cpp:2861-2864). Ordered by time, in prod 2026-08-23..09-22, a trip
// sent right after such an announcement for its source was ignored 26 times on 12 utility
// devices, three still missing from status when the window ended; ordered by arrival, a stale
// copy of a trip delivered after its clear showed the alert again twice, for 28 s and 805 s.
// Everything else goes by when it happened, so a daily arriving months late does not replace
// newer ones.
export const orderOf = (product: Product | undefined, row: Publish): Moment =>
  kindOf(row) === 'alert'
    ? arrival(row).at
    : profileFor(product).occurred(row).at;

// One publish however many times Particle handed it to us: a copy has the same published_at to
// the millisecond and a new sk (measured 70-137 s later, across every product, 2026-09-21).
export const deliveryKey = (row: Publish): string =>
  [get(row, 'coreid'), get(row, 'event'), row.published_at].join('|');

// One event however many times it was delivered or the device sent it.
export const eventKey = (
  product: Product | undefined,
  row: Publish
): string => {
  const same = profileFor(product).sameEvent(row);
  return same === undefined
    ? deliveryKey(row)
    : [get(row, 'coreid'), kindOf(row), ...same].join('|');
};

// The rows with every repeat left out, whichever way it arose, keeping the first of each in the
// order given.
export const distinct = (product: Product | undefined, rows: Publish[]) =>
  uniqBy(uniqBy(rows, deliveryKey), (row) => eventKey(product, row));
